import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';
import { useAppContext } from '../../libs/contextLib';
import { filterItems } from './BookingFilters';
import useAllCharters from '../Charters/useAllCharters';
import useAllSchedules from '../../hooks/useAllSchedules';
import { charterModelData } from '../../services/ModelService';
import { Charter } from '../../model/charter';
import dayjs from '../../dayjs';

const BookingContext = createContext();

export function BookingProvider({ children }) {
    const { messageApi } = useAppContext();
    const { allCharters, allChartersArray, loading } = useAllCharters();
    const { allSchedules } = useAllSchedules();
    const [filterBy, setFilterBy] = useState([]);
    const [filter, setFilter] = useState('');
    const [updated, setUpdated] = useState(false);
    const [saving, setSaving] = useState(false);

    // Callbacks for events
    const [callbacks, setCallbacks] = useState({});

    // Register a callback for a specific event
    const registerCallback = useCallback((event, callback) => {
        setCallbacks((prevCallbacks) => ({
            ...prevCallbacks,
            [event]: [...(prevCallbacks[event] || []), callback],
        }));
    }, []);

    // Unregister a callback for a specific event
    const unregisterCallback = useCallback((event, callback) => {
        setCallbacks((prevCallbacks) => ({
            ...prevCallbacks,
            [event]: (prevCallbacks[event] || []).filter((cb) => cb !== callback),
        }));
    }, []);

    // Trigger callbacks for a specific event
    const triggerCallbacks = useCallback(
        (event, ...args) => {
            if (callbacks[event]) {
                callbacks[event].forEach((callback) => callback(...args));
            }
        },
        [callbacks]
    );

    // OPTIMIZATION: Cache date parsing to avoid repeated dayjs operations
    const dateCache = useRef(new Map());

    // Helper function to get charter dates (moved from BookingList)
    const getCharterDates = useCallback((charter, allSchedules) => {
        if (charter.charterType === 'recurring') {
            // Don't process recurring charters if schedules aren't loaded yet
            if (!allSchedules || Object.keys(allSchedules).length === 0) {
                return [];
            }
            
            return charter.getRunningDates(allSchedules, 365);
        }
        
        if (charter.dates?.length) {
            return charter.dates;
        }
        
        return charter.startDate ? [charter.startDate] : [];
    }, []);

    // Enrich bookings with charterDates upfront to avoid recalculation
    const enrichedBookings = useMemo(() => {
        if (!allChartersArray?.length) return [];
        
        return allChartersArray.map(booking => ({
            ...booking,
            charterDates: getCharterDates(booking, allSchedules)
        }));
    }, [allChartersArray, allSchedules, getCharterDates]);

    // Memoized values
    const filteredBookings = useMemo(() => {
        if (!enrichedBookings?.length) return [];
        let filtered = [...enrichedBookings];

        // Apply status filters
        if (filterBy.length) {
            filtered = filtered.filter((booking) =>
                filterBy.some((filter) => filterItems[filter].showFn(booking))
            );
        }

        // Apply text search
        if (filter) {
            const searchTerm = filter.toLowerCase();
            filtered = filtered.filter(
                (booking) =>
                    booking.description?.toLowerCase().includes(searchTerm) ||
                    booking.bookingReference?.toLowerCase().includes(searchTerm) || 
                    booking.name?.toLowerCase().includes(searchTerm) ||
                    booking.customer?.name?.toLowerCase().includes(searchTerm)
            );
        }

        return filtered;
    }, [filterBy, filter, enrichedBookings]);

    // OPTIMIZATION: Move expensive grouping calculations to context
    const groupedBookings = useMemo(() => {
        if (!filteredBookings) return {};
      
        // Use native Date comparison for sorting (much faster than dayjs)
        const preSortedBookings = [...filteredBookings].sort((a, b) => {
            const dateA = new Date(a.startDate);
            const dateB = new Date(b.startDate);
            return dateA.getTime() - dateB.getTime();
        });
      
        const grouped = preSortedBookings.reduce((acc, charter) => {
            const dates = charter.charterDates || [];
            
            if (dates.length === 0) {
                acc['No Date'] = acc['No Date'] || [];
                acc['No Date'].push(charter);
                return acc;
            }
      
            dates.forEach(date => {
                // Cache formatted date keys to avoid repeated formatting
                let dateKey;
                if (dateCache.current.has(date)) {
                    dateKey = dateCache.current.get(date);
                } else {
                    dateKey = date?.format ? date.format('YYYY-MM-DD') : null;
                    if (dateKey) {
                        dateCache.current.set(date, dateKey);
                    }
                }
                
                if (dateKey) {
                    acc[dateKey] = acc[dateKey] || [];
                    acc[dateKey].push(charter);
                } else {
                    acc['No Date'] = acc['No Date'] || [];
                    acc['No Date'].push(charter);
                }
            });
            return acc;
        }, {});
      
        return grouped;
    }, [filteredBookings]);

    // OPTIMIZATION: Smart date range generation based on actual data
    const dateRange = useMemo(() => {
        const validDateKeys = Object.keys(groupedBookings).filter(dateKey => dateKey !== 'No Date');
        
        // --- Filtered View (Filter or filterBy active) ---
        if (filter || filterBy.length > 0) {
            return validDateKeys.sort();
        }

        // --- Normal View (No filters active) ---
        if (validDateKeys.length === 0) {
            // No bookings: show 1 month around today
            const today = new Date();
            const oneMonthAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
            const oneMonthFromNow = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
            
            const range = [];
            const currentDate = new Date(oneMonthAgo);
            while (currentDate <= oneMonthFromNow) {
                range.push(currentDate.toISOString().split('T')[0]);
                currentDate.setDate(currentDate.getDate() + 1);
            }
            return range;
        }

        // Has bookings: expand range to include all booking dates + 2 months buffer
        const bookingDates = validDateKeys.map(key => new Date(key)).sort((a, b) => a - b);
        const earliestBooking = bookingDates[0];
        const latestBooking = bookingDates[bookingDates.length - 1];
        
        // Add 2 months buffer before earliest and after latest booking
        const startDate = new Date(earliestBooking.getTime() - (60 * 24 * 60 * 60 * 1000));
        const endDate = new Date(latestBooking.getTime() + (60 * 24 * 60 * 60 * 1000));
        
        const range = [];
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
            range.push(currentDate.toISOString().split('T')[0]);
            currentDate.setDate(currentDate.getDate() + 1);
        }
        
        return range;
    }, [groupedBookings, filter, filterBy]);

    const createBooking = useCallback(async (booking) => {
        setSaving(true);
        try {
            const charter = new Charter(booking);
            await charterModelData.save(charter);
            setUpdated(true);
            triggerCallbacks('bookingCreated', charter);
        } catch (err) {
            console.error('Failed to create booking:', err);
        } finally {
            setSaving(false);
        }
    }, [triggerCallbacks]);

    const updateBooking = useCallback(async (booking) => {
        setSaving(true);
        try {
            const charter = new Charter(booking);
            await charterModelData.save(charter);
            setUpdated(true);
            triggerCallbacks('bookingUpdated', charter);
        } catch (err) {
            console.error('Failed to update booking:', err);
        } finally {
            setSaving(false);
        }
    }, [triggerCallbacks]);

    const deleteBooking = useCallback(async (bookingId) => {
        setSaving(true);
        try {
            await charterModelData.delete(bookingId);
            setUpdated(true);
            triggerCallbacks('bookingDeleted', bookingId);
        } catch (err) {
            console.error('Failed to delete booking:', err);
        } finally {
            setSaving(false);
        }
    }, [triggerCallbacks]);

    const value = {
        bookings: allChartersArray,
        charters: allChartersArray,
        filteredBookings,
        groupedBookings, // OPTIMIZATION: Pre-calculated grouped data
        dateRange, // OPTIMIZATION: Pre-calculated date range
        isLoading: !allChartersArray,
        filterBy,
        setFilterBy,
        filter,
        setFilter,
        updated,
        setUpdated,
        saving,
        createBooking,
        updateBooking,
        deleteBooking,
        registerCallback,
        unregisterCallback,
        triggerCallbacks,
        allCharters,
        allChartersArray,
        enrichedBookings,
        allSchedules,
        loading
    };

    return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export function useBooking() {
    const context = useContext(BookingContext);
    if (!context) {
        throw new Error('useBooking must be used within a BookingProvider');
    }
    return context;
} 